
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useModalBackButton } from "@/hooks/useModalBackButton"
import { visualLog } from "@/components/debug/VisualDebugger"
import { useKeyboardState } from "@/hooks/useKeyboardState"
import { useViewportHeight } from "@/hooks/useViewportHeight"
import { useKeyboardAwareScrolling, type ScrollOptions } from "@/hooks/useKeyboardAwareScrolling"
import { useKeyboardInset } from "@/hooks/useKeyboardInset"
import { useFocusIntoView } from "@/hooks/useFocusIntoView"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[5000] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    scrollRef?: React.RefObject<HTMLDivElement>
    scrollOptions?: ScrollOptions
    containerClassName?: string
    overlayClassName?: string
  }
>(({ 
  className,
  children,
  scrollRef,
  scrollOptions,
  containerClassName,
  overlayClassName,
  ...props
}, ref) => {
  // Trova un elemento DialogDescription nei figli se presente
  const hasDescription = React.Children.toArray(children).some(
    (child) =>
      React.isValidElement(child) &&
      child.type === DialogDescription
  )

  // Extract id and type-safe onOpenChange
  const { id } = props;

  // Gestione tasto back per le modali
  const onOpenChange = props['onOpenChange' as keyof typeof props] as
    ((open: boolean) => void) | undefined;

  const backButtonHandler = useModalBackButton({
    isOpen: true, // il componente esiste solo se è aperto
    modalName: id || 'dialog',
    onOpenChange: (open) => {
      if (onOpenChange) {
        onOpenChange(open);
      }
    }
  });
  
  // Usa i nuovi hook per gestire viewport e tastiera
  useViewportHeight();
  const { isKeyboardOpen } = useKeyboardState();
  const internalScrollRef = React.useRef<HTMLDivElement>(null);
  const containerRef = scrollRef ?? internalScrollRef;
  const fallbackRef = React.useRef<HTMLDivElement>(null);
  const contentRef = (ref as React.RefObject<HTMLDivElement>) ?? fallbackRef;

  useKeyboardInset(containerRef, 24);
  useFocusIntoView(containerRef);
  useKeyboardAwareScrolling(containerRef, {
    offsetTop: 60,
    offsetBottom: 60,
    behavior: 'smooth',
    block: 'center',
    ...scrollOptions,
  });

  // Gestione focus iniziale e disattivazione background
  React.useEffect(() => {
    const root = document.getElementById("root") as HTMLElement | null;
    if (root) {
      const prevHidden = root.getAttribute("aria-hidden");
      const prevInert = (root as any).inert;
      root.setAttribute("aria-hidden", "true");
      (root as any).inert = true;
      return () => {
        if (prevHidden !== null) root.setAttribute("aria-hidden", prevHidden);
        else root.removeAttribute("aria-hidden");
        if (typeof prevInert === "boolean") (root as any).inert = prevInert;
        else (root as any).inert = false;
      };
    }
    return;
  }, []);

  React.useEffect(() => {
    const first = contentRef.current?.querySelector<HTMLElement>(
      "button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    first?.focus();
  }, []);

  // Create a new props object with the updated onOpenChange
  const enhancedProps = {
    ...props,
    onOpenChange: backButtonHandler.handleOpenChange
  };

  const {
    onOpenChange: _,
    onInteractOutside,
    onPointerDownOutside,
    style,
    ...safeProps
  } = enhancedProps;

  // Calcola il posizionamento dinamico basato sulla tastiera
  const getDialogPositioning = () => {
    const maxHeight = 'max-h-[calc(var(--viewport-height)*0.85)]';
    if (isKeyboardOpen) {
      // Mantieni un limite di altezza per consentire lo scroll anche con tastiera aperta
      return `top-[5%] translate-y-0 ${maxHeight}`;
    }
    // Posizionamento normale centrato
    return `top-[50%] translate-y-[-50%] ${maxHeight}`;
  };
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref ?? fallbackRef}
        className={cn(
          "fixed left-[50%] z-[5001] w-full max-w-lg border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg translate-x-[-50%] viewport-transition",
          getDialogPositioning(),
          className
        )}
        // Se non c'è una descrizione esplicita, forniamo un aria-describedby
        aria-describedby={hasDescription ? undefined : "dialog-description"}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Escape") {
            e.preventDefault();
            // La chiusura avverrà tramite il meccanismo standard di Radix UI
          }
        }}
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;
          if (
            target.closest('[role="listbox"]') ||
            target.closest('[data-radix-select-content]') ||
            target.closest('.react-select__menu') ||
            target.closest('.react-select__menu-list') ||
            target.closest('.react-select__option')
          ) {
            e.preventDefault();
          }
          if (typeof onPointerDownOutside === 'function') {
            onPointerDownOutside(e);
          }
        }}
        onInteractOutside={(e) => {
          if (typeof onInteractOutside === 'function') {
            onInteractOutside(e);
          }
        }}
        style={style as React.CSSProperties}
        {...safeProps}
      >
        <div
          ref={containerRef}
          className={cn(
            "max-h-[calc(var(--viewport-height)-10%)] overflow-y-auto overscroll-contain px-4 py-4 keyboard-aware-scroll",
            containerClassName
          )}
        >
          {children}
          {!hasDescription && (
            <span id="dialog-description" className="sr-only">
              Finestra di dialogo
            </span>
          )}
        </div>
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
